"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Moon, Sun } from "lucide-react"
import { flushSync } from "react-dom"

import { cn } from "@/utils"

interface AnimatedThemeTogglerProps
  extends React.ComponentPropsWithoutRef<"button"> {
  duration?: number
}

type ThemeMode = "light" | "dark"

const applyThemeToDocument = (theme: ThemeMode) => {
  const root = document.documentElement
  if (theme === "dark") {
    root.classList.add("dark")
  } else {
    root.classList.remove("dark")
  }
  root.dataset.theme = theme
  try {
    localStorage.setItem("theme", theme)
  } catch {
    // Ignore write failures (privacy mode, etc.)
  }
}

const animateRipple = (
  node: HTMLButtonElement,
  duration: number,
  root: HTMLElement
) => {
  const { top, left, width, height } = node.getBoundingClientRect()
  const x = left + width / 2
  const y = top + height / 2
  const maxRadius = Math.hypot(
    Math.max(left, window.innerWidth - left),
    Math.max(top, window.innerHeight - top)
  )

  root.animate(
    {
      clipPath: [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${maxRadius}px at ${x}px ${y}px)`,
      ],
    },
    {
      duration,
      easing: "ease-in-out",
      pseudoElement: "::view-transition-new(root)",
    }
  )
}

export const AnimatedThemeToggler = ({
  className,
  duration = 400,
  ...props
}: AnimatedThemeTogglerProps) => {
  const [isDark, setIsDark] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const root = document.documentElement
    const updateTheme = () => {
      setIsDark(root.classList.contains("dark") || root.dataset.theme === "dark")
    }

    updateTheme()

    const observer = new MutationObserver(updateTheme)
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    })

    return () => observer.disconnect()
  }, [])

  const toggleTheme = useCallback(async () => {
    const button = buttonRef.current
    if (!button) return

    const root = document.documentElement
    const nextTheme: ThemeMode = isDark ? "light" : "dark"

    const commitTheme = () => {
      setIsDark(nextTheme === "dark")
      applyThemeToDocument(nextTheme)
    }

    const startTransition = ((document as typeof document & {
      startViewTransition?: (cb: () => void) => {
        ready: Promise<void>
      }
    }).startViewTransition)?.bind(document)

    if (typeof startTransition === "function") {
      const transition = startTransition(() => {
        flushSync(commitTheme)
      })
      await transition.ready
      animateRipple(button, duration, root)
    } else {
      flushSync(commitTheme)
      animateRipple(button, duration, root)
    }
  }, [isDark, duration])

  return (
    <button
      ref={buttonRef}
      onClick={toggleTheme}
      className={cn(className)}
      {...props}
    >
      {isDark ? <Sun /> : <Moon />}
      <span className="sr-only">Toggle theme</span>
    </button>
  )
}
